import { useEffect, useState } from 'react';
import {
  Settings as SettingsIcon,
  RefreshCw,
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  DollarSign,
  ToggleLeft,
  Clock,
  Database,
  Shield,
  Eye,
  EyeOff,
} from 'lucide-react';

type SettingValueType = 'string' | 'integer' | 'decimal' | 'boolean' | 'json';

interface Setting {
  key: string;
  value: unknown;
  value_type: SettingValueType | null;
  description: string | null;
  updated_by: string | null;
  updated_at: string;
  created_at: string;
}

const VALUE_TYPE_OPTIONS: SettingValueType[] = ['string', 'integer', 'decimal', 'boolean', 'json'];

function effectiveValueType(vt: string | null | undefined): SettingValueType {
  return (VALUE_TYPE_OPTIONS as string[]).includes(vt ?? '')
    ? (vt as SettingValueType)
    : 'string';
}

function displayValue(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

interface GroupedSettings {
  pricing: Setting[];
  features: Setting[];
  retention: Setting[];
  other: Setting[];
}

export default function AdminSettings() {
  const [settings, setSettings] = useState<Setting[]>([]);
  const [grouped, setGrouped] = useState<GroupedSettings>({
    pricing: [],
    features: [],
    retention: [],
    other: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Edit state
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editValueType, setEditValueType] = useState<SettingValueType>('string');
  const [saving, setSaving] = useState(false);

  // New setting state
  const [showNewForm, setShowNewForm] = useState(false);
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [newValueType, setNewValueType] = useState<SettingValueType>('string');
  const [newDescription, setNewDescription] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/admin/settings`, {
        credentials: 'include',
      });

      const data = (await response.json()) as any;

      if (data.success) {
        setSettings(data.settings || []);
        setGrouped(data.grouped || { pricing: [], features: [], retention: [], other: [] });
      } else {
        setError(data.error || 'Failed to fetch settings');
      }
    } catch (err) {
      console.error('[Settings] Error:', err);
      setError('Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (setting: Setting) => {
    setEditingKey(setting.key);
    setEditValue(displayValue(setting.value));
    setEditValueType(effectiveValueType(setting.value_type));
  };

  const handleCancelEdit = () => {
    setEditingKey(null);
    setEditValue('');
    setEditValueType('string');
  };

  const handleSave = async (key: string) => {
    setSaving(true);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/admin/settings/${key}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ value: editValue, value_type: editValueType }),
      });

      const data = (await response.json()) as any;

      if (data.success) {
        setEditingKey(null);
        setEditValue('');
        fetchSettings();
      } else {
        alert(data.error || 'Failed to update setting');
      }
    } catch (err) {
      console.error('[Settings] Save error:', err);
      alert('Failed to update setting');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (key: string) => {
    if (!confirm(`Are you sure you want to delete "${key}"?`)) {
      return;
    }

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/admin/settings/${key}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      const data = (await response.json()) as any;

      if (data.success) {
        fetchSettings();
      } else {
        alert(data.error || 'Failed to delete setting');
      }
    } catch (err) {
      console.error('[Settings] Delete error:', err);
      alert('Failed to delete setting');
    }
  };

  const handleCreateNew = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/admin/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          key: newKey,
          value: newValue,
          value_type: newValueType,
          description: newDescription || null,
        }),
      });

      const data = (await response.json()) as any;

      if (data.success) {
        setShowNewForm(false);
        setNewKey('');
        setNewValue('');
        setNewValueType('string');
        setNewDescription('');
        fetchSettings();
      } else {
        alert(data.error || 'Failed to create setting');
      }
    } catch (err) {
      console.error('[Settings] Create error:', err);
      alert('Failed to create setting');
    } finally {
      setSaving(false);
    }
  };

  // Protected keys that cannot be deleted
  const protectedKeys = [
    'default_landline_cost',
    'default_landline_price',
    'default_tcpa_cost',
    'default_tcpa_price',
  ];

  if (loading && settings.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">System Settings</h1>
          <p className="text-gray-500 mt-1">Configure application behavior and defaults</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={fetchSettings}
            disabled={loading}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => setShowNewForm(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" />
            <span>Add Setting</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">{error}</div>
      )}

      {/* New Setting Form */}
      {showNewForm && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Add New Setting</h3>
          <form onSubmit={handleCreateNew} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Key</label>
                <input
                  type="text"
                  value={newKey}
                  onChange={(e) => setNewKey(e.target.value)}
                  placeholder="e.g., feature_name_enabled"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={newValueType}
                  onChange={(e) => setNewValueType(e.target.value as SettingValueType)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  {VALUE_TYPE_OPTIONS.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Value</label>
                {newValueType === 'boolean' ? (
                  <select
                    value={newValue || 'true'}
                    onChange={(e) => setNewValue(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    <option value="true">true</option>
                    <option value="false">false</option>
                  </select>
                ) : (
                  <input
                    type={newValueType === 'integer' || newValueType === 'decimal' ? 'number' : 'text'}
                    step={newValueType === 'integer' ? '1' : newValueType === 'decimal' ? 'any' : undefined}
                    value={newValue}
                    onChange={(e) => setNewValue(e.target.value)}
                    placeholder="Setting value"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description (optional)
              </label>
              <input
                type="text"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                placeholder="What this setting does"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowNewForm(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Creating...' : 'Create Setting'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Pricing Settings */}
      {grouped.pricing.length > 0 && (
        <SettingGroup
          title="Pricing"
          description="Cost and price defaults"
          icon={DollarSign}
          iconBg="bg-green-100"
          iconColor="text-green-600"
          settings={grouped.pricing}
          editingKey={editingKey}
          editValue={editValue}
          setEditValue={setEditValue}
          editValueType={editValueType}
          setEditValueType={setEditValueType}
          onEdit={handleEdit}
          onSave={handleSave}
          onCancel={handleCancelEdit}
          onDelete={handleDelete}
          saving={saving}
          protectedKeys={protectedKeys}
        />
      )}

      {/* Feature Settings */}
      {grouped.features.length > 0 && (
        <SettingGroup
          title="Features"
          description="Feature toggles and flags"
          icon={ToggleLeft}
          iconBg="bg-purple-100"
          iconColor="text-purple-600"
          settings={grouped.features}
          editingKey={editingKey}
          editValue={editValue}
          setEditValue={setEditValue}
          editValueType={editValueType}
          setEditValueType={setEditValueType}
          onEdit={handleEdit}
          onSave={handleSave}
          onCancel={handleCancelEdit}
          onDelete={handleDelete}
          saving={saving}
          protectedKeys={protectedKeys}
        />
      )}

      {/* Retention Settings */}
      {grouped.retention.length > 0 && (
        <SettingGroup
          title="Retention"
          description="Data retention and TTL settings"
          icon={Clock}
          iconBg="bg-orange-100"
          iconColor="text-orange-600"
          settings={grouped.retention}
          editingKey={editingKey}
          editValue={editValue}
          setEditValue={setEditValue}
          editValueType={editValueType}
          setEditValueType={setEditValueType}
          onEdit={handleEdit}
          onSave={handleSave}
          onCancel={handleCancelEdit}
          onDelete={handleDelete}
          saving={saving}
          protectedKeys={protectedKeys}
        />
      )}

      {/* Other Settings */}
      {grouped.other.length > 0 && (
        <SettingGroup
          title="Other"
          description="General configuration"
          icon={Database}
          iconBg="bg-blue-100"
          iconColor="text-blue-600"
          settings={grouped.other}
          editingKey={editingKey}
          editValue={editValue}
          setEditValue={setEditValue}
          editValueType={editValueType}
          setEditValueType={setEditValueType}
          onEdit={handleEdit}
          onSave={handleSave}
          onCancel={handleCancelEdit}
          onDelete={handleDelete}
          saving={saving}
          protectedKeys={protectedKeys}
        />
      )}

      {settings.length === 0 && !loading && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <SettingsIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Settings</h3>
          <p className="text-gray-500 mb-4">
            System settings will appear here once configured.
          </p>
          <button
            onClick={() => setShowNewForm(true)}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Setting
          </button>
        </div>
      )}
    </div>
  );
}

// Helper to detect sensitive keys that should be blurred
const SENSITIVE_PATTERNS = [
  'key',
  'secret',
  'password',
  'token',
  'credential',
  'auth',
  'api_key',
  'apikey',
];

function isSensitiveKey(key: string): boolean {
  const lowerKey = key.toLowerCase();
  return SENSITIVE_PATTERNS.some((pattern) => lowerKey.includes(pattern));
}

// Setting Group Component
function SettingGroup({
  title,
  description,
  icon: Icon,
  iconBg,
  iconColor,
  settings,
  editingKey,
  editValue,
  setEditValue,
  editValueType,
  setEditValueType,
  onEdit,
  onSave,
  onCancel,
  onDelete,
  saving,
  protectedKeys,
}: {
  title: string;
  description: string;
  icon: typeof DollarSign;
  iconBg: string;
  iconColor: string;
  settings: Setting[];
  editingKey: string | null;
  editValue: string;
  setEditValue: (value: string) => void;
  editValueType: SettingValueType;
  setEditValueType: (t: SettingValueType) => void;
  onEdit: (setting: Setting) => void;
  onSave: (key: string) => void;
  onCancel: () => void;
  onDelete: (key: string) => void;
  saving: boolean;
  protectedKeys: string[];
}) {
  // Track which sensitive values are revealed
  const [revealedKeys, setRevealedKeys] = useState<Set<string>>(new Set());

  const toggleReveal = (key: string) => {
    setRevealedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center space-x-3">
          <div className={`${iconBg} p-2 rounded-lg`}>
            <Icon className={`w-5 h-5 ${iconColor}`} />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            <p className="text-sm text-gray-500">{description}</p>
          </div>
        </div>
      </div>

      <div className="divide-y divide-gray-200">
        {settings.map((setting) => {
          const isEditing = editingKey === setting.key;
          const isProtected = protectedKeys.includes(setting.key);

          return (
            <div key={setting.key} className="px-6 py-4 hover:bg-gray-50">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <p className="text-sm font-medium text-gray-900 font-mono">{setting.key}</p>
                    {setting.value_type && (
                      <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 rounded font-mono">
                        {setting.value_type}
                      </span>
                    )}
                    {isProtected && (
                      <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-800 rounded">
                        <Shield className="w-3 h-3 mr-1" />
                        Protected
                      </span>
                    )}
                  </div>

                  {setting.description && (
                    <p className="text-sm text-gray-500 mt-1">{setting.description}</p>
                  )}

                  {isEditing ? (
                    <div className="mt-2 flex items-center space-x-2">
                      <select
                        value={editValueType}
                        onChange={(e) => setEditValueType(e.target.value as SettingValueType)}
                        className="px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm bg-white"
                        title="Value type"
                      >
                        {VALUE_TYPE_OPTIONS.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                      {editValueType === 'boolean' ? (
                        <select
                          value={editValue === 'true' ? 'true' : 'false'}
                          onChange={(e) => setEditValue(e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm bg-white"
                          autoFocus
                        >
                          <option value="true">true</option>
                          <option value="false">false</option>
                        </select>
                      ) : (
                        <input
                          type={editValueType === 'integer' || editValueType === 'decimal' ? 'number' : 'text'}
                          step={editValueType === 'integer' ? '1' : editValueType === 'decimal' ? 'any' : undefined}
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                          autoFocus
                        />
                      )}
                      <button
                        onClick={() => onSave(setting.key)}
                        disabled={saving}
                        className="p-2 text-green-600 hover:bg-green-50 rounded-lg disabled:opacity-50"
                      >
                        <Save className="w-4 h-4" />
                      </button>
                      <button
                        onClick={onCancel}
                        className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="mt-2 flex items-center space-x-2">
                      <p
                        className={`text-sm font-semibold text-gray-900 font-mono bg-gray-100 px-3 py-2 rounded-lg inline-block select-none ${
                          isSensitiveKey(setting.key) && !revealedKeys.has(setting.key)
                            ? 'blur-sm'
                            : ''
                        }`}
                      >
                        {displayValue(setting.value)}
                      </p>
                      {isSensitiveKey(setting.key) && (
                        <button
                          onClick={() => toggleReveal(setting.key)}
                          className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                          title={revealedKeys.has(setting.key) ? 'Hide value' : 'Reveal value'}
                        >
                          {revealedKeys.has(setting.key) ? (
                            <EyeOff className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </button>
                      )}
                    </div>
                  )}

                  {setting.updated_by && (
                    <p className="text-xs text-gray-400 mt-2">
                      Updated by {setting.updated_by} on{' '}
                      {new Date(setting.updated_at).toLocaleDateString()}
                    </p>
                  )}
                </div>

                {!isEditing && (
                  <div className="flex items-center space-x-1 ml-4">
                    <button
                      onClick={() => onEdit(setting)}
                      className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                      title="Edit"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    {!isProtected && (
                      <button
                        onClick={() => onDelete(setting.key)}
                        className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
